import React from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  Container
} from '@mui/material';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const DRAWER_WIDTH = 240;

const AdminLayoutSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Navigation Drawer Skeleton */}
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {/* Logo/Brand Area */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton circle width={40} height={40} />
              <Skeleton height={24} width={120} />
            </Box>
          </Box>

          {/* Navigation Menu */}
          <List sx={{ pt: 2 }}>
            {[...Array(8)].map((_, index) => (
              <ListItem key={index} sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Skeleton circle width={24} height={24} />
                  <Skeleton height={16} width={100} />
                </Box>
              </ListItem>
            ))}
          </List>

          {/* Bottom Section */}
          <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton circle width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton height={14} width={80} />
                <Skeleton height={12} width={60} style={{ marginTop: 2 }} />
              </Box>
            </Box>
            <Skeleton height={32} width="100%" />
          </Box>
        </Drawer>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          {/* Top App Bar Skeleton */}
          <AppBar
            position="sticky"
            sx={{
              width: `calc(100% - ${DRAWER_WIDTH}px)`,
              ml: `${DRAWER_WIDTH}px`,
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: 1,
            }}
          >
            <Toolbar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Skeleton height={24} width={200} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton circle width={32} height={32} />
                <Skeleton circle width={32} height={32} />
                <Skeleton circle width={40} height={40} />
              </Box>
            </Toolbar>
          </AppBar>

          {/* Page Content */}
          <Container maxWidth="xl" sx={{ mt: 3, mb: 4, flex: 1 }}>
            {/* Page Title */}
            <Box sx={{ mb: 3 }}>
              <Skeleton height={40} width={200} style={{ marginBottom: 8 }} />
              <Skeleton height={20} width={400} />
            </Box>

            {/* Content Grid */}
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              {[...Array(6)].map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 3,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Skeleton height={24} width="60%" style={{ marginBottom: 16 }} />
                  <Skeleton height={16} count={3} style={{ marginBottom: 8 }} />
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Skeleton height={32} width={80} />
                    <Skeleton height={32} width={100} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      </Box>
    </SkeletonTheme>
  );
};

export default AdminLayoutSkeleton;
